<script lang="ts">
  import {
    loadStripe,
    type Stripe,
    type StripeCardElement,
  } from "@stripe/stripe-js";
  import {
    BodyText,
    Button,
    Header,
    Hor,
    Label,
    ModalDialog,
    Spacer,
    Toast,
    Ver,
    WithResource,
  } from "$components";
  import api, {
    type ApiResponse,
    asApi,
    asError,
    fromApi,
  } from "$shared/lib/api-parkito2.svelte.ts";
  import { writable } from "svelte/store";
  import type { PaymentCardSetup } from "$apis/parkito2";

  const {
    debtorId,
    onCardAdded,
    debtorSetupCard = (debtorId: string) =>
      api.debtorSetupCard({ path: { id: debtorId } }),
    debtorFinalizeCardSetup = (setupId: string, debtorId: string) =>
      api.debtorFinalizeCardSetup({
        path: {
          debtorId: debtorId,
          setupId,
        },
        body: {
          cardName: "Kort",
        },
      }),
  }: {
    debtorId: string;
    onCardAdded: () => void;
    debtorSetupCard?: (
      debtorId: string,
    ) => Promise<ApiResponse<PaymentCardSetup>>;
    debtorFinalizeCardSetup?: (
      setupId: string,
      debtorId: string,
    ) => Promise<ApiResponse<unknown>>;
  } = $props();

  const cardSetupCall = fromApi(() => {
    $previousCardSetupSuccessFull = false;
    return debtorSetupCard(debtorId);
  });

  let isDialogOpen = $state(false);
  export async function openDialog(): Promise<void> {
    isDialogOpen = true;
    await $cardSetupCall.load();
  }
  export function closeDialog() {
    isDialogOpen = false;
  }

  const previousCardSetupSuccessFull = writable(false);
  const stripeCall = fromApi((publicKey: string) =>
    asApi(
      loadStripe(publicKey).then((stripe) => {
        if (stripe === null) {
          throw Error("Inget svar från stripe");
        } else {
          return stripe;
        }
      }),
    )
  );
  $cardSetupCall.onOk((cs) => {
    if (cs.status === "Succeeded") {
      $previousCardSetupSuccessFull = true;
    } else {
      $stripeCall.load(cs.publicKey);
    }
  });
  const cardElement = $stripeCall.map((stripe) => {
    let elements = stripe!.elements({
      clientSecret: $cardSetupCall.call.data!.stripeSecret,
    });
    let payment = elements.create("card", {
      classes: { base: "card-input" },
      iconStyle: "solid",
      disableLink: true,
      hidePostalCode: true,
    });
    return payment;
  });
  $cardElement.onOk((cardElement) => {
    cardElement.mount("#card-element");
  });

  async function waitForSetupIntent(
    stripe: Stripe,
    clientSecret: string,
    {
      intervalMs = 3000,
      timeoutMs = 60_000,
    } = {},
  ) {
    const start = Date.now();

    while (true) {
      const { setupIntent, error } = await stripe.retrieveSetupIntent(
        clientSecret,
      );

      if (error) throw error;

      if (setupIntent.status === "succeeded") {
        return setupIntent;
      }

      if (
        setupIntent.status === "canceled"
      ) {
        throw new Error("Validering avbröts");
      }

      if (setupIntent.status as unknown === "failed") {
        throw new Error("Validering misslyckades");
      }

      if (Date.now() - start > timeoutMs) {
        throw new Error("Timed out waiting for 3DS completion");
      }

      await new Promise((r) => setTimeout(r, intervalMs));
    }
  }

  const confirmCardSetupCall = fromApi(
    async (
      stripe: Stripe,
      card: StripeCardElement,
      clientSecret: string,
    ) => {
      try {
        // TODO både nya och "trasiga" payment card setups har status requiresPaymentMethod
        // Hur vet jag om det är en trasig som redan har kört confirmCardSetup så jag kan hoppa över dethär steget?
        const setup = await stripe.confirmCardSetup(
          clientSecret,
          {
            payment_method: {
              card: card,
            },
          },
        );

        if (setup.error) {
          return asError(setup.error.message, "stripe");
        }

        const intent = await waitForSetupIntent(stripe, clientSecret);

        if (intent.status === "succeeded") {
          const res = await debtorFinalizeCardSetup(
            $cardSetupCall.call.data!.id,
            debtorId,
          );
          return res;
        } else {
          return asError(
            "Validering avbröts. Status: " + intent.status,
            "stripe",
          );
        }
      } catch (err: unknown) {
        console.log("xxxx got errr", err);
        return asError(err, "stripe");
      }
    },
  );

  $confirmCardSetupCall.onOk((res) => {
    onCardAdded();
  });
  $confirmCardSetupCall.onErr(() => {
    Toast.error("Något gick fel");
  });
</script>

{#if isDialogOpen}
  <ModalDialog title="Lägg till kort">
    <div class="max-mobile:w-full w-[400px]">
      <Ver margin="none">
        <div id="card-element"></div>

        {#if $confirmCardSetupCall.call.error}
          <Label type="error">{$confirmCardSetupCall.call.error.message}</Label>
        {/if}

        <WithResource api={cardSetupCall}>
          {#snippet error(err)}
            <BodyText>{err}</BodyText>
            <Button text="Stäng" onClick={closeDialog}></Button>
          {/snippet}
          {#snippet child(cardSetup)}
            {#if $previousCardSetupSuccessFull}
              <BodyText>Ditt kort har lagts till!</BodyText>
              <Button text="Stäng" onClick={onCardAdded}></Button>
            {:else}
              <WithResource api={stripeCall}>
                {#snippet child(stripe)}
                  <WithResource api={cardElement}>
                    {#snippet child(cardElementVal)}
                      <Hor>
                        <Button
                          type="ghost"
                          text="Avbryt"
                          onClick={() =>
                            !$confirmCardSetupCall.isLoading() &&
                            closeDialog()}
                        />
                        <Spacer />
                        <Button
                          onClick={() =>
                            $confirmCardSetupCall.load(
                              stripe,
                              cardElementVal,
                              cardSetup.stripeSecret,
                            )}
                          text="Lägg till"
                          loading={$confirmCardSetupCall.isLoading()}
                        />
                      </Hor>
                    {/snippet}
                  </WithResource>
                {/snippet}
              </WithResource>
            {/if}
          {/snippet}
        </WithResource>
      </Ver>
    </div>
  </ModalDialog>
{/if}

<style lang="postcss">
  :global(.card-input) {
    border-bottom: 1px solid;
    padding: 0 0 1rem 0;
    margin: 0.5rem 0 1rem 0;
  }
</style>
